import React, { RefObject, useCallback, useEffect, useRef } from 'react'

type Offset = { x: number; y: number }

const KEYBOARD_STEP = 16
const KEYBOARD_LARGE_STEP = 64
const KEYBOARD_DIRECTIONS: Record<string, Offset> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
}

/**
 * Moves a fixed-position panel by translating it from where its own CSS places it, keeping it inside the viewport.
 * The offset is written straight to the panel's style so dragging never re-renders the panel's contents.
 */
export const useDraggablePanel = (panelRef: RefObject<HTMLElement | null>) => {
  const offset = useRef<Offset>({ x: 0, y: 0 })
  const dragStart = useRef<{ pointerId: number; x: number; y: number; offset: Offset } | null>(null)

  const moveTo = useCallback(
    (next: Offset) => {
      const panel = panelRef.current
      if (!panel) return
      const rect = panel.getBoundingClientRect()
      // The rect includes the current translation; remove it to get where the panel's CSS places it.
      const baseLeft = rect.left - offset.current.x
      const baseTop = rect.top - offset.current.y
      const { clientWidth, clientHeight } = document.documentElement
      // Clamp the far edge first so a panel larger than the viewport keeps its top-left corner (and header) visible.
      // Whole pixels keep the panel's text and border sharp.
      const x = Math.round(Math.max(-baseLeft, Math.min(next.x, clientWidth - rect.width - baseLeft)))
      const y = Math.round(Math.max(-baseTop, Math.min(next.y, clientHeight - rect.height - baseTop)))
      offset.current = { x, y }
      panel.style.transform = x || y ? `translate(${x}px, ${y}px)` : ''
    },
    [panelRef],
  )

  useEffect(() => {
    const keepInViewport = () => moveTo(offset.current)
    window.addEventListener('resize', keepInViewport)
    return () => window.removeEventListener('resize', keepInViewport)
  }, [moveTo])

  // Marks the handle while dragging so it can keep its pressed look; :active is unreliable once mousedown is cancelled.
  const endDrag = (handle: HTMLElement) => {
    dragStart.current = null
    delete handle.dataset.dragging
  }

  const onPointerEnd = (event: React.PointerEvent<HTMLElement>) => {
    if (dragStart.current?.pointerId === event.pointerId) endDrag(event.currentTarget)
  }

  return {
    // Stops text selection while dragging. Cancelling mousedown rather than pointerdown keeps the event firing, so
    // menus and popovers that close on an outside mousedown still close.
    onMouseDown: (event: React.MouseEvent<HTMLElement>) => event.preventDefault(),
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      // Ctrl+click opens the context menu on macOS, which can swallow the pointerup.
      if (event.button !== 0 || event.ctrlKey) return
      event.currentTarget.setPointerCapture(event.pointerId)
      event.currentTarget.dataset.dragging = 'true'
      dragStart.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, offset: offset.current }
    },
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
      const start = dragStart.current
      if (!start || start.pointerId !== event.pointerId) return
      // The button was released without a pointerup reaching us (e.g. a context menu or window switch took it).
      if (event.buttons === 0) return endDrag(event.currentTarget)
      moveTo({ x: start.offset.x + event.clientX - start.x, y: start.offset.y + event.clientY - start.y })
    },
    onPointerUp: onPointerEnd,
    onPointerCancel: onPointerEnd,
    onLostPointerCapture: onPointerEnd,
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      const direction = KEYBOARD_DIRECTIONS[event.key]
      // Leave modified arrows to the browser and OS (e.g. Alt+Left is Back).
      if (!direction || event.altKey || event.ctrlKey || event.metaKey) return
      event.preventDefault()
      const step = event.shiftKey ? KEYBOARD_LARGE_STEP : KEYBOARD_STEP
      moveTo({ x: offset.current.x + direction.x * step, y: offset.current.y + direction.y * step })
    },
  }
}
